import { Attrs, Node as ProseMirrorNode } from 'prosemirror-model';
import { EditorState, TextSelection, Transaction } from 'prosemirror-state';
import { CellSelection } from '../editorExtensions/tableElementContent/cellSelection';
import { Editor } from '@tiptap/core';

function removeAndUpdateMark(
  tr: Transaction,
  node: ProseMirrorNode,
  pos: number,
  attribute?: string,
  style?: Record<string, string>,
) {
  if (node.marks.length > 0) {
    ['textStyle', 'link'].forEach((markStyle) => {
      const textStyleMark = node.marks.find((mark) => mark.type.name === markStyle);
      if (!textStyleMark?.attrs) return;
      const updateAttr: Record<string, null | string> = {};
      if (attribute !== undefined) {
        updateAttr[attribute] = null;
        if (textStyleMark.type.name === 'link' && style && style[attribute] !== undefined) {
          updateAttr[attribute] = style[attribute];
        }
      }
      tr.removeMark(pos, pos + node.nodeSize, textStyleMark?.type);
      const newMark = textStyleMark.type.create({
        ...textStyleMark.attrs,
        ...updateAttr,
      });
      tr.addMark(pos, pos + node.nodeSize, newMark);
    });
  }
}
export function updateInnerChildMarks(
  child: ProseMirrorNode,
  tr: Transaction,
  style: Record<string, string>,
  childAbsolutePos: number,
) {
  child.descendants((inner, innerPos) => {
    inner.marks.forEach((mark) => {
      if ([
        'textStyle',
        'span',
        'strong',
        'bold',
        'em',
        'italic',
      ].includes(mark.type.name) && mark.type.create) {
        const temp = document.createElement('div');
        temp.style.cssText = mark.attrs.style || '';
        Object.assign(temp.style, style);

        const newMark = mark.type.create({
          ...mark.attrs,
          style: temp.style.cssText,
        });

        const absPos = childAbsolutePos + 1 + innerPos;
        tr.removeMark(absPos, absPos + inner.nodeSize, mark.type);
        tr.addMark(absPos, absPos + inner.nodeSize, newMark);
        temp.remove();
      }
    });
  });
}

function checkContentOverlap(
  node: ProseMirrorNode,
  from: number,
  to: number,
  pos: number,
): boolean {
  let hasContentOverlap = false;

  const checkChild = (child: ProseMirrorNode, offset: number) => {
    if (!['bulletList', 'orderedList'].includes(child.type.name)) {
      const childStart = pos + 1 + offset;
      const childEnd = childStart + child.nodeSize;
      if (from <= childEnd && to >= childStart) {
        hasContentOverlap = true;
      } else if (from === to && from >= childStart && from <= childEnd) {
        hasContentOverlap = true;
      }
    }
  };

  if (node.content && node.content.forEach) {
    node.content.forEach(checkChild);
  } else if (node.forEach) {
    node.forEach(checkChild);
  }

  return hasContentOverlap;
}

function updateNestedListColors(
  node: ProseMirrorNode,
  pos: number,
  tr: Transaction,
  style?: Record<string, string>,
) {
  if (!style?.color || !node.forEach) return;

  node.forEach((child, offset) => {
    if (['bulletList', 'orderedList'].includes(child.type.name)) {
      const childPos = pos + 1 + offset;
      const currentStyle = (child.attrs?.style as string) || '';
      if (!/color\s*:/.test(currentStyle)) {
        const temp = document.createElement('div');
        temp.style.cssText = currentStyle;
        temp.style.color = 'initial';
        tr.setNodeMarkup(childPos, child.type, {
          ...child.attrs,
          style: temp.style.cssText,
        });
        temp.remove();
      }
    }
  });
}

function isSelectionFullyInNestedList(
  node: ProseMirrorNode,
  from: number,
  to: number,
  pos: number,
): boolean {
  let selectionFullyInNested = false;
  if (node.forEach) {
    node.forEach((child, offset) => {
      if (['bulletList', 'orderedList'].includes(child.type.name)) {
        const childStart = pos + 1 + offset;
        const childEnd = childStart + child.nodeSize;
        if (from >= childStart && to <= childEnd) {
          selectionFullyInNested = true;
        }
      }
    });
  }
  return selectionFullyInNested;
}

function applyStylesToNode(
  node: ProseMirrorNode,
  pos: number,
  from: number,
  to: number,
  tr: Transaction,
  style?: Record<string, string>,
  attrs?: Attrs,
) {
  const dom = document.createElement('div');
  dom.style.cssText = (node.attrs.style as string) || '';
  Object.assign(dom.style, style);

  // Sanitize styles
  if (style?.color) {
    dom.style.color = style.color;
  }

  // Specifically handle nested list styles
  if (['bulletList', 'orderedList'].includes(node.type.name) && style?.color) {
    dom.style.color = 'initial';
  }

  const finalStyle = dom.style.cssText;
  const isListItem = node.type.name === 'listItem';

  // Handle list item specific logic
  if (isListItem) {
    checkContentOverlap(node, from, to, pos);
    updateNestedListColors(node, pos, tr, style);
  }

  // Update node markup if selection is not fully in nested list
  if (!isSelectionFullyInNestedList(node, from, to, pos)) {
    tr.setNodeMarkup(pos, node.type, { ...node.attrs, style: finalStyle, ...attrs });
  }

  // Update inner child marks for paragraphs
  node.descendants((child, childPos) => {
    if (child.type.name === 'paragraph') {
      const childAbsolutePos = pos + 1 + childPos;
      updateInnerChildMarks(child, tr, style ?? {}, childAbsolutePos);
    }
  });

  dom.remove();
}

function updateNodeAttributesForRange(
  state: EditorState,
  from: number,
  to: number,
  typeNames: string | string[],
  nodes: ProseMirrorNode[],
  tr: Transaction,
  attribute?: string,
  style?: Record<string, string>,
  attrs?: Attrs,
) {
  if (from < 0 || to > state.doc.content.size || from > to) {
    return;
  }
  state.doc.nodesBetween(from, to, (node, pos) => {
    const parentElement = state.doc.resolve(pos)?.parent;
    if (typeNames.includes(node.type.name) && parentElement?.type?.name !== 'iconTag') {
      nodes.push(node);
      if (node.isText) {
        removeAndUpdateMark(tr, node, pos, attribute, style);
      } else {
        applyStylesToNode(node, pos, from, to, tr, style, attrs);
      }
    }
    return true;
  });
}

export function updateNodeStylesInRange(
  typeNames: string[],
  style: Record<string, string>,
  attribute: string,
  fromValue?: number,
  toValue?: number,
) {
  return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
    const nodes: ProseMirrorNode[] = [];
    const tr = state.tr;
    const sel = state.selection;
    if (sel instanceof CellSelection) {
      sel.forEachCell((cell, pos) => {
        const from = fromValue ?? pos;
        const to = toValue ?? pos + cell.nodeSize - 1;
        updateNodeAttributesForRange(state, from, to, typeNames, nodes, tr, attribute, style);
      });
    } else {
      const from = fromValue ?? sel.from;
      const to = toValue ?? sel.to;
      updateNodeAttributesForRange(state, from, to, typeNames, nodes, tr, attribute, style);
    }
    if (nodes.length < 1) {
      return false;
    }
    if (dispatch) {
      dispatch(tr);
    }
    return true;
  };
}

export function applyListPaddingFromFontSize(
  node: ProseMirrorNode,
  pos: number,
  tr: Transaction,
  style: Record<string, string>,
) {
  const fontSize = style?.fontSize;
  if (!fontSize) return;

  const applyPadding = (targetNode: ProseMirrorNode, targetPos: number) => {
    const existingStyle = targetNode.attrs?.style ?? '';
    const temp = document.createElement('div');
    temp.style.cssText = existingStyle;
    temp.style.paddingLeft = fontSize;
    const finalStyle = temp.style.cssText;
    tr.setNodeMarkup(targetPos, targetNode.type, {
      ...targetNode.attrs,
      style: finalStyle,
    });

    temp.remove();
  };

  if (['bulletList', 'orderedList'].includes(node.type.name)) {
    applyPadding(node, pos);
  }
}

export function updateNestedListStyle(
  editor: Editor,
  newListType: string,
) {
  const { state, view } = editor;
  const { tr, schema } = state;
  const { $from } = state.selection;

  const bulletStyles = ['disc', 'circle', 'square'];
  const orderedStyles = ['1', 'a', 'i', 'lower-alpha', 'lower-roman'];

  let targetNodeTypeName: string | null = null;
  if (bulletStyles.includes(newListType)) {
    targetNodeTypeName = 'bulletList';
  } else if (orderedStyles.includes(newListType)) {
    targetNodeTypeName = 'orderedList';
  }

  if (!targetNodeTypeName) return false;
  const targetNodeType = schema.nodes[targetNodeTypeName];

  const styleMap: Record<string, string> = {
    '1': 'decimal',
    a: 'lower-alpha',
    'lower-alpha': 'lower-alpha',
    i: 'lower-roman',
    'lower-roman': 'lower-roman',
    disc: 'disc',
    circle: 'circle',
    square: 'square',
  };
  const cssStyle = styleMap[newListType] || 'decimal';

  let listPos = -1;
  let listNode: ProseMirrorNode | null = null;

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
      let isNested = false;
      for (let p = d - 1; p > 0; p--) {
        if ($from.node(p).type.name === 'listItem') {
          isNested = true;
          break;
        }
      }

      if (isNested) {
        listNode = node;
        listPos = $from.before(d);
        break;
      }
    }
  }

  if (!listNode || listPos === -1) return false;

  // Check parent list compatibility for merging
  const parentList = $from.node($from.depth - 2);
  if (
    parentList &&
    (parentList.type.name === 'bulletList' || parentList.type.name === 'orderedList')
  ) {
    const parentStyle = parentList.attrs.listStyleType ||
      (parentList.type.name === 'bulletList' ? 'disc' : 'decimal');

    if (parentList.type.name === targetNodeTypeName && parentStyle === cssStyle) {
      return editor.commands.liftListItem('listItem');
    }
  }

  if (listNode.type.name !== targetNodeTypeName) {
    tr.setNodeMarkup(listPos, targetNodeType, {
      ...listNode.attrs,
      listStyleType: cssStyle,
    });
  } else {
    tr.setNodeMarkup(listPos, null, {
      ...listNode.attrs,
      listStyleType: cssStyle,
    });
  }

  if (tr.docChanged) {
    const { from, to } = state.selection;
    const newSelection = TextSelection.create(tr.doc, from, to);
    tr.setSelection(newSelection);
    view.dispatch(tr);
    view.focus();
    return true;
  }

  return false;
}
